import { MetricsResponse, BenchmarkingIndexValues } from 'API';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export const useJoinData = (
  mainData: Array<any> | undefined,
  mainTotalData: Array<any> | undefined,
  ccData: Array<any> | undefined,
  ccTotalData: Array<any> | undefined,
  benchmarkData: Array<any> | undefined,
  benchmarkTotalData: Array<any> | undefined,
  isGraph: boolean,
  mainQueryStatus: 'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  mainTotalQueryStatus: 'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  ccQueryStatus: 'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  ccTotalQueryStatus: 'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  benchmarkQueryStatus: 'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  benchmarkTotalQueryStatus: 'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  chartMetrics: Array<MetricsResponse>,
  setChartMetrics: Dispatch<SetStateAction<Array<MetricsResponse>>>,
  componentID: string,
  // costPerCC: string,
  newQuery?: 'OLD' | 'NEW' | 'BACK' | 'APPLY',
  defaultSelected?: boolean | undefined,
  benchmarkIndex?: BenchmarkingIndexValues | null,
): readonly [any] => {
  // NOTE: used to be without useEffect and only returns data, but it was not working properly when edit from page7
  const [data, setData] = useState<any>(undefined);
  // console.log('BENCH QUERY FETCHED', benchmarkQueryStatus);
  // Helper function to get benchmark column name from data
  const getBenchmarkColumnName = (dataWithBenchmark: any[]) => {
    if (dataWithBenchmark.length === 0) return null;
    return Object.keys(dataWithBenchmark[0]).find((key) => key.includes(' benchmark')) || null;
  };

  // Helper function to get ALL benchmark column names from data
  const getAllBenchmarkColumnNames = (dataWithBenchmark: any[]) => {
    if (dataWithBenchmark.length === 0) return [];
    return Object.keys(dataWithBenchmark[0]).filter((key) => key.includes(' benchmark'));
  };

  // Helper function to merge benchmark data with main data
  const addBenchmarkDataToRows = (
    dataRows: any[],
    benchmarkData: any[],
    benchmarkIndex?: BenchmarkingIndexValues | null,
  ) => {
    if (!benchmarkData || benchmarkData.length === 0) {
      return dataRows;
    }

    // Get all dimension columns (string values) from main data, excluding specific columns
    const mainDimensionColumns =
      dataRows.length > 0
        ? Object.keys(dataRows[0]).filter((key) => {
            const value = dataRows[0][key];
            // Include only string values that are not obviously metrics
            return (
              typeof value === 'string' &&
              value !== '-' && // Exclude calculated/missing values
              value !== 'Infinity' && // Exclude invalid values
              !key.includes('benchmark') && // Exclude existing benchmark columns
              !key.includes('Cost per') && // Exclude calculated metrics
              key !== 'lineitem' && // Exclude lineitem as it's too specific
              key !== 'campaign_id'
            ); // Exclude campaign_id
          })
        : [];

    // Get benchmark dimension columns - dynamically from benchmark data
    const benchmarkDimensionColumns =
      benchmarkData.length > 0
        ? Object.keys(benchmarkData[0]).filter((key) => {
            const value = benchmarkData[0][key];
            // Only include string columns that are valid dimensions
            return typeof value === 'string' && value !== '-' && value !== 'Infinity' && !key.includes('benchmark');
          })
        : [];

    // Use only dimensions that exist in both datasets
    const dimensionColumns = mainDimensionColumns.filter((col) => benchmarkDimensionColumns.includes(col));

    // Find all valid benchmark entries with numeric values
    const validBenchmarkEntries: Array<{
      dimensions: { [key: string]: string };
      metric: string;
      value: number;
    }> = [];

    benchmarkData.forEach((benchmark) => {
      // Get all string dimension keys and their values
      const dimensionKeys = Object.keys(benchmark).filter((key) => {
        const value = benchmark[key];
        return typeof value === 'string' && value !== '-' && value !== 'Infinity' && dimensionColumns.includes(key);
      });

      // Get all numeric metrics
      const numericKeys = Object.keys(benchmark).filter((key) => {
        const value = benchmark[key];
        return typeof value === 'number' && isFinite(value) && value !== null && !isNaN(value);
      });

      // Create entries for each valid numeric metric
      numericKeys.forEach((metricKey) => {
        const dimensions: { [key: string]: string } = {};
        dimensionKeys.forEach((dimKey) => {
          dimensions[dimKey] = benchmark[dimKey];
        });

        validBenchmarkEntries.push({
          dimensions,
          metric: metricKey,
          value: benchmark[metricKey],
        });
      });
    });

    // Group benchmark entries by metric name
    const benchmarksByMetric: { [metricName: string]: Map<string, number> } = {};

    validBenchmarkEntries.forEach(({ dimensions, metric, value }) => {
      if (!benchmarksByMetric[metric]) {
        benchmarksByMetric[metric] = new Map();
      }

      // Create dimension key for matching
      const dimensionKey = dimensionColumns.map((col) => dimensions[col] || '').join('_');

      benchmarksByMetric[metric].set(dimensionKey, value);
    });

    // Add benchmark data to main data rows with proper column ordering
    let dataWithBenchmark = [...dataRows];

    // First pass: add all benchmark columns
    Object.keys(benchmarksByMetric).forEach((metricName) => {
      const benchmarkMap = benchmarksByMetric[metricName];
      const benchmarkColumnName = `${metricName} benchmark`;

      dataWithBenchmark = dataWithBenchmark.map((row) => {
        // Create dimension key from main data row
        const dimensionKey = dimensionColumns.map((col) => row[col] || '').join('_');
        const benchmarkValue = benchmarkMap.get(dimensionKey);

        return {
          ...row,
          [benchmarkColumnName]: benchmarkValue !== undefined ? benchmarkValue : '-',
        };
      });
    });

    // Calculate benchmark index if enabled
    if (
      benchmarkIndex &&
      (benchmarkIndex === BenchmarkingIndexValues.PERCENTAGE || benchmarkIndex === BenchmarkingIndexValues.INDEX)
    ) {
      Object.keys(benchmarksByMetric).forEach((metricName) => {
        const benchmarkColumnName = `${metricName} benchmark`;
        const indexColumnName =
          benchmarkIndex === BenchmarkingIndexValues.PERCENTAGE
            ? `${metricName} benchmark %`
            : `${metricName} benchmark index`;

        dataWithBenchmark = dataWithBenchmark.map((row) => {
          const metricValue = row[metricName];
          const benchmarkValue = row[benchmarkColumnName];

          let indexValue: string | number = '-';

          // Calculate index if both values are numeric and benchmark is not zero
          if (
            typeof metricValue === 'number' &&
            typeof benchmarkValue === 'number' &&
            benchmarkValue !== 0 &&
            isFinite(metricValue) &&
            isFinite(benchmarkValue)
          ) {
            if (benchmarkIndex === BenchmarkingIndexValues.PERCENTAGE) {
              // Calculate percentage change: ((value - benchmark) / benchmark) * 100
              const percentageChange = ((metricValue - benchmarkValue) / benchmarkValue) * 100;
              indexValue = Math.round(percentageChange);
            } else {
              // INDEX: Show index as percentage (ratio * 100)
              const ratio = metricValue / benchmarkValue;
              indexValue = Math.round(ratio * 100);
            }
          }

          return {
            ...row,
            [indexColumnName]: indexValue,
          };
        });
      });
    }

    // Now reorder columns to maintain original order and place benchmark columns after their respective metrics
    const reorderedData = dataWithBenchmark.map((row) => {
      if (dataRows.length === 0) return row;

      const originalColumns = Object.keys(dataRows[0]);
      const reorderedRow: any = {};

      originalColumns.forEach((colName) => {
        // Add the original column
        reorderedRow[colName] = row[colName];

        // Check if this column has benchmark data and add it immediately after
        const benchmarkColumnName = `${colName} benchmark`;
        if (row.hasOwnProperty(benchmarkColumnName)) {
          reorderedRow[benchmarkColumnName] = row[benchmarkColumnName];
        }

        // Check if this column has benchmark percentage/index and add it after benchmark
        const benchmarkPercentageColumnName = `${colName} benchmark %`;
        if (row.hasOwnProperty(benchmarkPercentageColumnName)) {
          reorderedRow[benchmarkPercentageColumnName] = row[benchmarkPercentageColumnName];
        }

        const benchmarkIndexColumnName = `${colName} benchmark index`;
        if (row.hasOwnProperty(benchmarkIndexColumnName)) {
          reorderedRow[benchmarkIndexColumnName] = row[benchmarkIndexColumnName];
        }
      });

      return reorderedRow;
    });

    // Sort by all dimension columns to ensure proper grouping for merged cells
    const sortedData = reorderedData.sort((a, b) => {
      for (const col of dimensionColumns) {
        const aValue = String(a[col] || '');
        const bValue = String(b[col] || '');
        const comparison = aValue.localeCompare(bValue);
        if (comparison !== 0) {
          return comparison;
        }
      }
      return 0;
    });

    return sortedData;
  };

  // TODO: Problem is that when clicking Apply should not update the chartMetrics, except when I add cost per custo
  useEffect(() => {
    const chartMetricsSet = (data: any) => {
      if (isGraph && data && data.length > 0) {
        // console.log('USE JOIN DATA RERENDER', newQuery, chartMetrics);
        if (newQuery === 'NEW' || newQuery === 'APPLY') {
          // Only update chartMetrics if they're empty or significantly different
          // This prevents unnecessary re-renders
          const mostkeys = data.sort((a: any, b: any) => Object.keys(b).length - Object.keys(a).length)[0];
          const arr = [];

          if (defaultSelected) {
            const mostKeys = data.sort((a: any, b: any) => Object.keys(b).length - Object.keys(a).length)[0];
            const metricKey = Object.keys(mostKeys).find((el: any) => typeof mostKeys[el] !== 'string');
            arr.push({
              chartType: 'line',
              metric: metricKey,
              axis: 0,
            } as MetricsResponse);
          } else {
            // Use the exact order of columns as they appear in the data
            // This preserves the order from the database query
            const allKeys = Object.keys(mostkeys);

            for (const key of allKeys) {
              const value = mostkeys[key];
              if (
                typeof value === 'number' ||
                value === 'Infinity' ||
                value === 'NaN' ||
                key === 'Start date' ||
                key === 'End date' ||
                value === null
              ) {
                arr.push({
                  chartType: 'column',
                  metric: key,
                  axis: arr.length,
                } as MetricsResponse);
              }
            }
          }

          // Only update if metrics are actually different to prevent re-render loops
          const metricsChanged =
            chartMetrics.length !== arr.length ||
            arr.some((newMetric, index) => !chartMetrics[index] || chartMetrics[index].metric !== newMetric.metric);
          // console.log('MERTIKS3', chartMetrics, arr, metricsChanged);
          if (metricsChanged) {
            // console.log('MERTIKS4', arr);
            setChartMetrics(arr);
          }
        } else if (newQuery === 'BACK') {
          const mostkeys = data.sort((a: any, b: any) => Object.keys(b).length - Object.keys(a).length)[0];

          // Collect all new metrics with proper ordering (benchmarks after original metrics)
          const newMetrics: any[] = [];
          const processedKeys = new Set<string>();

          for (const [key, value] of Object.entries(mostkeys)) {
            if (
              typeof value === 'number' ||
              value === 'Infinity' ||
              value === 'NaN' ||
              key === 'Start date' ||
              key === 'End date' ||
              value === null
            ) {
              // Skip if already processed (e.g., benchmark metrics that were added with their original)
              if (processedKeys.has(key)) {
                continue;
              }

              const existingMetric = chartMetrics.find((metric: any) => metric.metric === key);
              if (!existingMetric) {
                // Add the main metric first
                newMetrics.push({
                  chartType: 'column',
                  metric: key,
                  axis: chartMetrics.length + newMetrics.length,
                });
                processedKeys.add(key);

                // Check for benchmark metrics and add them immediately after
                const benchmarkKey = key + ' benchmark';
                const benchmarkPercentageKey = key + ' benchmark %';
                const benchmarkIndexKey = key + ' benchmark index';

                if (
                  mostkeys.hasOwnProperty(benchmarkKey) &&
                  !chartMetrics.find((metric: any) => metric.metric === benchmarkKey)
                ) {
                  newMetrics.push({
                    chartType: 'column',
                    metric: benchmarkKey,
                    axis: chartMetrics.length + newMetrics.length,
                  });
                  processedKeys.add(benchmarkKey);
                }

                if (
                  mostkeys.hasOwnProperty(benchmarkPercentageKey) &&
                  !chartMetrics.find((metric: any) => metric.metric === benchmarkPercentageKey)
                ) {
                  newMetrics.push({
                    chartType: 'column',
                    metric: benchmarkPercentageKey,
                    axis: chartMetrics.length + newMetrics.length,
                  });
                  processedKeys.add(benchmarkPercentageKey);
                }

                if (
                  mostkeys.hasOwnProperty(benchmarkIndexKey) &&
                  !chartMetrics.find((metric: any) => metric.metric === benchmarkIndexKey)
                ) {
                  newMetrics.push({
                    chartType: 'column',
                    metric: benchmarkIndexKey,
                    axis: chartMetrics.length + newMetrics.length,
                  });
                  processedKeys.add(benchmarkIndexKey);
                }
              }
            }
          }

          // Add all new metrics in one batch to avoid multiple state updates
          if (newMetrics.length > 0) {
            // console.log('MERTIKS5', newMetrics);
            setChartMetrics((ps: any) => [...ps, ...newMetrics]);
          }
        }
      }

      setData(data);
    };
    setData(undefined);

    const isAnyQueryRunningOrFailed = [
      mainQueryStatus,
      mainTotalQueryStatus,
      ccQueryStatus,
      ccTotalQueryStatus,
      benchmarkQueryStatus,
      benchmarkTotalQueryStatus,
    ].some((status) => status === 'RUNNING' || status === 'FAILED');

    if (!isAnyQueryRunningOrFailed) {
      const howmanydone = [
        mainQueryStatus,
        mainTotalQueryStatus,
        ccQueryStatus,
        ccTotalQueryStatus,
        benchmarkQueryStatus,
        benchmarkTotalQueryStatus,
      ].filter((status) => status === 'DONE').length;
      // console.log('ALL STATUS HOW MANY DONE', howmanydone, componentID);
      const howmanyDataArrays = [
        mainData,
        mainTotalData,
        ccData,
        ccTotalData,
        benchmarkData,
        benchmarkTotalData,
      ].filter((arr) => arr /* && arr.length > 0 */).length;
      // console.log('ALL STATUS HOW MANY DATA ARRAYS', howmanyDataArrays, componentID);

      if (howmanydone === howmanyDataArrays) {
        if (!isGraph) {
          if (ccQueryStatus === 'NOT STARTED') {
            console.log('MAIN TOTAL DATA JOINING WITHOUT CC', mainData, mainTotalData);
            if (mainData && mainTotalData) {
              // Add benchmark data to main data first (before adding total row)
              let processedData = [...mainData];
              if (benchmarkQueryStatus === 'DONE' && benchmarkData && benchmarkData.length > 0) {
                processedData = addBenchmarkDataToRows(processedData, benchmarkData, benchmarkIndex);
              }

              if (mainTotalData.length === 0) {
                chartMetricsSet(processedData);
                return;
              }

              // Create total row after benchmark processing
              const allProperties = { ...mainData[0], ...mainTotalData[0] };
              const totalRow = Object.keys(allProperties).reduce((obj, key) => {
                return { ...obj, [key]: mainTotalData[0][key] };
              }, {});

              // Add ALL benchmark columns to total row if they exist in processed data
              const benchmarkColumnNames = getAllBenchmarkColumnNames(processedData);
              if (benchmarkColumnNames.length > 0) {
                benchmarkColumnNames.forEach((benchmarkColumnName) => {
                  // Try to get benchmark total value, otherwise use '-'
                  let benchmarkTotalValue: string | number = '-';
                  if (benchmarkTotalData && benchmarkTotalData.length > 0) {
                    // Find the metric name by removing ' benchmark' from column name
                    const baseMetricName = benchmarkColumnName.replace(' benchmark', '');
                    const benchmarkTotal = benchmarkTotalData[0];

                    // Look for the metric value in benchmarkTotalData
                    if (benchmarkTotal && typeof benchmarkTotal[baseMetricName] === 'number') {
                      benchmarkTotalValue = benchmarkTotal[baseMetricName];
                    }
                  }
                  (totalRow as any)[benchmarkColumnName] = benchmarkTotalValue;
                });
              }

              // Separate main data and total row, then combine
              const finalData = [...processedData, totalRow];
              chartMetricsSet(finalData);
            }
          } else if (ccQueryStatus === 'DONE' && ccTotalQueryStatus === 'DONE') {
            if (mainData && mainTotalData && ccData && ccTotalData) {
              let costPerCC = 'Cost per';
              // ccData is a list of objects, and I need to find the one that non of the values are null, undefined or NaN
              const costPerCC2 =
                ccData &&
                (ccData as any[]).find(
                  (cc: any) =>
                    Object.values(cc).every(
                      (v: any) => (typeof v === 'string' && v !== 'Infinity' && v !== 'NaN') || typeof v === 'number',
                    ) &&
                    cc !== null &&
                    cc !== undefined,
                );

              if (costPerCC2) {
                const key = Object.entries(costPerCC2).find(
                  ([k, v]) => typeof v === 'number' && k !== 'custom_conversion',
                )?.[0];
                costPerCC = key ?? 'Cost per';
              }

              // TODO ADD THE OTHER 2 AS WELL
              const conversionName =
                ccData.length > 0
                  ? ccData[0].hasOwnProperty('conversion_name')
                    ? 'conversion_name'
                    : ccData[0].hasOwnProperty('filter_name')
                      ? 'filter_name'
                      : ccData[0].hasOwnProperty('activity')
                        ? 'activity'
                        : ccData[0].hasOwnProperty('floodlight_activity_name')
                          ? 'floodlight_activity_name'
                          : 'CC_name'
                  : '';

              const conversionValue =
                ccData.length > 0
                  ? ccData[0].hasOwnProperty('conversion_name')
                    ? 'custom_conversions_value'
                    : ccData[0].hasOwnProperty('filter_name')
                      ? 'custom_conversions'
                      : ccData[0].hasOwnProperty('activity')
                        ? 'total_conversions'
                        : ccData[0].hasOwnProperty('floodlight_activity_name')
                          ? 'total_conversions'
                          : 'custom_conversion'
                  : '';
              const transformedMainData = mainData.map((mainItem) => {
                const matchingCCDataItems = ccData.filter((ccItem) => {
                  const {
                    [conversionName]: CC_name,
                    [conversionValue]: custom_conversion,
                    [costPerCC]: costper,
                    ...rest
                  } = ccItem;
                  return Object.entries(rest).every(([key, value]) => mainItem[key] === value);
                });

                if (matchingCCDataItems.length > 0) {
                  return matchingCCDataItems.reduce(
                    (
                      acc,
                      { [conversionName]: CC_name, [conversionValue]: custom_conversion, [costPerCC]: costper },
                    ) => {
                      if (costper) {
                        return { ...acc, [CC_name]: custom_conversion, [`${costPerCC} ${CC_name}`]: costper };
                      } else {
                        return { ...acc, [CC_name]: custom_conversion };
                      }
                    },
                    mainItem,
                  );
                } else {
                  return { ...mainItem };
                }
              });

              const transformedTotalData = mainTotalData.map((mainItem) => {
                const matchingCCTotalDataItems = ccTotalData.filter((ccItem) => {
                  const {
                    [conversionName]: CC_name,
                    [conversionValue]: custom_conversion,
                    [costPerCC]: costper,
                    ...rest
                  } = ccItem;
                  return Object.entries(rest).every(([key, value]) => mainItem[key] === value);
                });

                if (matchingCCTotalDataItems.length > 0) {
                  return matchingCCTotalDataItems.reduce(
                    (
                      acc,
                      { [conversionName]: CC_name, [conversionValue]: custom_conversion, [costPerCC]: costper },
                    ) => {
                      if (costper) {
                        return { ...acc, [CC_name]: custom_conversion, [`${costPerCC} ${CC_name}`]: costper };
                      } else {
                        return { ...acc, [CC_name]: custom_conversion };
                      }
                    },
                    mainItem,
                  );
                } else {
                  return { ...mainItem };
                }
              });

              // Process main data with benchmark first
              let processedMainData = transformedMainData;
              if (benchmarkQueryStatus === 'DONE' && benchmarkData && benchmarkData.length > 0) {
                processedMainData = addBenchmarkDataToRows(transformedMainData, benchmarkData, benchmarkIndex);
              }

              // If ccTotalData is empty, return just the processed main data without total row
              // if (ccTotalData.length === 0) {
              //   chartMetricsSet(processedMainData);
              //   return;
              // }

              // If transformedTotalData is empty, return just the processed main data without total row
              if (transformedTotalData.length === 0) {
                chartMetricsSet(processedMainData);
                return;
              }

              const allProperties = { ...transformedMainData[0], ...transformedTotalData[0] };
              // console.log("All properties", allProperties)
              const totalRow = Object.keys(allProperties).reduce((obj, key) => {
                // console.log("Key", key)
                // console.log("obj", obj)
                return { ...obj, [key]: transformedTotalData[0][key] };
              }, {});

              // Add ALL benchmark columns to total row if they exist in processed data
              const benchmarkColumnNames = getAllBenchmarkColumnNames(processedMainData);
              if (benchmarkColumnNames.length > 0) {
                benchmarkColumnNames.forEach((benchmarkColumnName) => {
                  // Try to get benchmark total value, otherwise use '-'
                  let benchmarkTotalValue: string | number = '-';
                  if (benchmarkTotalData && benchmarkTotalData.length > 0) {
                    // Find the metric name by removing ' benchmark' from column name
                    const baseMetricName = benchmarkColumnName.replace(' benchmark', '');
                    const benchmarkTotal = benchmarkTotalData[0];

                    // Look for the metric value in benchmarkTotalData
                    if (benchmarkTotal && typeof benchmarkTotal[baseMetricName] === 'number') {
                      benchmarkTotalValue = benchmarkTotal[baseMetricName];
                    }
                  }
                  (totalRow as any)[benchmarkColumnName] = benchmarkTotalValue;
                });
              }

              // Separate main data and total row, then combine
              const finalData = [...processedMainData, totalRow];
              chartMetricsSet(finalData);
            }
          }
        } else {
          if (ccQueryStatus === 'NOT STARTED') {
            if (mainData) {
              let graphData = mainData;

              // Add benchmark data if available for graphs
              if (benchmarkQueryStatus === 'DONE' && benchmarkData && benchmarkData.length > 0) {
                graphData = addBenchmarkDataToRows(graphData, benchmarkData, benchmarkIndex);
              }
              chartMetricsSet(graphData);
            }
          } else if (ccQueryStatus === 'DONE') {
            if (mainData && ccData) {
              let costPerCC = 'Cost per';
              // ccData is a list of objects, and I need to find the one that non of the values are null, undefined or NaN
              const costPerCC2 =
                ccData &&
                (ccData as any[]).find(
                  (cc: any) =>
                    Object.values(cc).every(
                      (v: any) => (typeof v === 'string' && v !== 'Infinity' && v !== 'NaN') || typeof v === 'number',
                    ) &&
                    cc !== null &&
                    cc !== undefined,
                );

              if (costPerCC2) {
                const key = Object.entries(costPerCC2).find(
                  ([k, v]) => typeof v === 'number' && k !== 'custom_conversion',
                )?.[0];
                costPerCC = key ?? 'Cost per';
              }

              const conversionName =
                ccData.length > 0
                  ? ccData[0].hasOwnProperty('conversion_name')
                    ? 'conversion_name'
                    : ccData[0].hasOwnProperty('filter_name')
                      ? 'filter_name'
                      : ccData[0].hasOwnProperty('activity')
                        ? 'activity'
                        : ccData[0].hasOwnProperty('floodlight_activity_name')
                          ? 'floodlight_activity_name'
                          : 'CC_name'
                  : '';
              const conversionValue =
                ccData.length > 0
                  ? ccData[0].hasOwnProperty('conversion_name')
                    ? 'custom_conversions_value'
                    : ccData[0].hasOwnProperty('filter_name')
                      ? 'custom_conversions'
                      : ccData[0].hasOwnProperty('activity')
                        ? 'total_conversions'
                        : ccData[0].hasOwnProperty('floodlight_activity_name')
                          ? 'total_conversions'
                          : 'custom_conversion'
                  : '';
              const transformedMainData = mainData.map((mainItem) => {
                const matchingCCDataItems = ccData.filter((ccItem) => {
                  const {
                    [conversionName]: CC_name,
                    [conversionValue]: custom_conversion,
                    [costPerCC]: costper,
                    ...rest
                  } = ccItem;
                  return Object.entries(rest).every(([key, value]) => mainItem[key] === value);
                });

                if (matchingCCDataItems.length > 0) {
                  return matchingCCDataItems.reduce(
                    (
                      acc,
                      { [conversionName]: CC_name, [conversionValue]: custom_conversion, [costPerCC]: costper },
                    ) => {
                      if (costper) {
                        return { ...acc, [CC_name]: custom_conversion, [`${costPerCC} ${CC_name}`]: costper };
                      } else {
                        return { ...acc, [CC_name]: custom_conversion };
                      }
                    },
                    mainItem,
                  );
                } else {
                  return { ...mainItem };
                }
              });
              chartMetricsSet(transformedMainData);
            }
          }
        }
      }
    }
  }, [
    ccData,
    ccQueryStatus,
    ccTotalData,
    ccTotalQueryStatus,
    benchmarkData,
    benchmarkTotalData,
    benchmarkQueryStatus,
    chartMetrics,
    defaultSelected,
    isGraph,
    mainData,
    mainQueryStatus,
    mainTotalData,
    mainTotalQueryStatus,
    newQuery,
    setChartMetrics,
    componentID,
    benchmarkTotalQueryStatus,
    benchmarkIndex,
  ]);
  return [data] as const;
};
